import { createApi } from "@reduxjs/toolkit/query/react";

import apiConfig from "configs/apiConfig";
import { REDUCER_PATHS } from "store/config";
import { transformResponseHandler } from "utils/transformResponseHandler";

import { customFetchBase } from ".";

const rewardsApi = createApi({
  reducerPath: REDUCER_PATHS.REWARDS,
  baseQuery: customFetchBase,
  endpoints: (builder) => ({
    rewardsList: builder.query({
      query: (data) => ({
        url: `${apiConfig.REWARDS}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data.searchText}&sortColumn=${data.sortColumn}&sortDirection=${data.sortDirection}&filter=${data.filterData}`,
        method: "GET",
      }),
      providesTags: ["RewardsList"],
    }),
    referredPayoutList: builder.query({
      query: (data) => ({
        url: `${apiConfig.REFFERED_PAYOUT}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data.searchText}&sortColumn=${data.sortColumn}&sortDirection=${data.sortDirection}&filter=${data.filterData}`,
        method: "GET",
      }),
      providesTags: ["RewardsList"],
    }),
    rewardsUserList: builder.query({
      query: (data) => ({
        url: `${apiConfig.REWARDS_USER}/?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data.searchText}&sortColumn=${data.sortColumn}&sortDirection=${data.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["RewardsList"],
    }),
    rewardsListChangeStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.REFFERED_STATUS,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["RewardsList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    rewardsListDelete: builder.mutation({
      query: (data) => ({
        url: apiConfig.REWARDS,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["RewardsList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    addRewardsList: builder.mutation({
      query: (data) => ({
        url: apiConfig.REWARDS,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["RewardsList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    rewardsDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.REWARDS_DETAILS}/${data.id}`,
        method: "GET",
      }),
      providesTags: ["RewardsList"],
    }),
    referralRewardsDetailsList: builder.query({
      query: (data) => ({
        url: `${apiConfig.REWARDS_DETAILS_LIST}/?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data.searchText}&sortColumn=${data.sortColumn}&sortDirection=${data.sortDirection}&referrerUserId=${data.referrerUserId}&filter=${data.filterData}`,
        method: "GET",
      }),
      providesTags: ["RewardsList"],
    }),
    rewardsListDownload: builder.mutation({
      query: (data) => {
        const params = new URLSearchParams();

        if (data.filter) params.append("filter", data.filter);
        if (data.column) params.append("column", data.column);

        return {
          url: `${apiConfig.REWARDS_DOWNLOAD}/${
            data.type
          }?${params.toString()}`,
          method: "GET",
        };
      },
      invalidatesTags: ["RewardsList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    rewardsPayoutDownload: builder.mutation({
      query: (data) => {
        const params = new URLSearchParams();

        if (data.filter) params.append("filter", data.filter);
        if (data.column) params.append("column", data.column);

        return {
          url: `${apiConfig.REWARDS_PAYOUT_DOWNLOAD}/${
            data.type
          }?${params.toString()}`,
          method: "GET",
        };
      },
      invalidatesTags: ["RewardsList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    rewardsReferredDownload: builder.mutation({
      query: (data) => {
        const params = new URLSearchParams();

        if (data.filter) params.append("filter", data.filter);
        if (data.column) params.append("column", data.column);

        return {
          url: `${apiConfig.REWARDS_REFFERED_DOWNLOAD}/${
            data.type
          }?${params.toString()}`,
          method: "GET",
        };
      },
      invalidatesTags: ["RewardsList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
  }),
});

export const {
  useAddRewardsListMutation,
  useLazyRewardsListQuery,
  useLazyRewardsUserListQuery,
  useRewardsListChangeStatusMutation,
  useRewardsListDeleteMutation,
  useLazyRewardsDetailsQuery,
  useLazyReferredPayoutListQuery,
  useLazyReferralRewardsDetailsListQuery,
  useRewardsListDownloadMutation,
  useRewardsPayoutDownloadMutation,
  useRewardsReferredDownloadMutation,
} = rewardsApi;

export default rewardsApi;
